import type { AgentThreadSummary, AgentMode, WorkspaceSummary } from '@tabbio/types';
import { EMPLOYER_EXTRA_NAV } from './launch-scope';
import { getWorkspaceChatHref } from './workspace-routes';

// ─── Types ───────────────────────────────────────────────────────────

export type NavCategory = 'primary' | 'customize';

export interface NavItem {
  label: string;
  iconName: string;
  href: string | null;
  type?: 'drawer' | 'push';
  hasChevron?: boolean;
  badgeKey?: 'messages' | 'notifications' | 'sharedCvs';
  category?: NavCategory;
  /** Non-interactive "coming soon" row: renders muted with a Soon label. */
  disabled?: boolean;
}

export type Workspace = WorkspaceSummary;
export type WebNavItem = NavItem;

export interface ThreadGroup {
  label: string;
  items: AgentThreadSummary[];
}

// ─── Workspace → Mode mapping ────────────────────────────────────────

export function workspaceToMode(ws: WorkspaceSummary): AgentMode {
  return ws.type === 'personal' ? 'seeker' : 'employer';
}

// ─── Nav Items (workspace-aware) ─────────────────────────────────────

/** Destinations that depend on the account (a server answer, never guessed). */
export interface NavOptions {
  /** Publishing is available: the Sites list (plan 7.10) joins after Skills. */
  sites?: boolean;
}

// Personal workspace, in render order. Inbox, Notifications, Upgrade and
// Memory are deliberately absent from this list: Inbox and Notifications live
// on the My Tabbio hub, Upgrade is the hub's plan pill and Memory stays under
// Settings, so the sidebar reads as a short list of destinations.
//
// The tour anchors on `iconName` (web, NAV_TOUR_TARGETS) and on the literal
// `href` (native, DrawerContent.toolTourTargetByHref, pinned by
// tour-captions.test.ts). Relabel freely; keep those two stable.
const personalNav = (t: (k: string) => string, options: NavOptions = {}): NavItem[] => [
  { label: t('nav.chat'), iconName: 'Home', href: '/(app)/chat', type: 'drawer' },
  // "My CV" opens the My Tabbio hub (identity + the CV list). The CV editors
  // are pushed above it from there, so Back always lands on the hub.
  { label: t('nav.myCv'), iconName: 'FileText', href: '/(app)/cv/my-tabbio', type: 'drawer', category: 'primary' },
  { label: t('nav.jobs'), iconName: 'Briefcase', href: '/(app)/jobs', type: 'drawer', category: 'primary' },
  // "Tasks" is the Automation board (Nova): the label changed, the route did not.
  { label: t('nav.tasks'), iconName: 'Workflow', href: '/auto-apply', type: 'push', category: 'primary' },
  // Connectors is NOT here on purpose: it moved under Settings (2026-09), so
  // the sidebar stays the short list of destinations the Figma spec shows.
  { label: t('nav.skills'), iconName: 'Skills', href: '/(app)/skills', type: 'drawer', category: 'customize' },
  // Sites (plan 7.10): only where publishing is available to this account.
  ...(options.sites
    ? ([{ label: t('nav.sites'), iconName: 'Globe', href: '/(app)/sites', type: 'drawer', category: 'customize' }] satisfies NavItem[])
    : []),
];

const companyNav = (t: (k: string) => string): NavItem[] => [
  { label: t('nav.tabbio'), iconName: 'Home', href: '/(employer)/chat', type: 'drawer' },
  { label: t('nav.company'), iconName: 'User', href: '/(employer)/company', type: 'drawer', category: 'primary' },
  // Phase 1: company-wide Applications inbox (jobs + talent network union).
  { label: t('nav.applications'), iconName: 'Users', href: '/(employer)/applications', type: 'drawer', category: 'primary' },
  // Phase 1 ships the Company Page first; the wider workspace shell follows.
  { label: t('nav.workspaceSoon'), iconName: 'Grid3X3', href: null, disabled: true, category: 'primary' },
  // Post-Phase-1 employer rows stay hidden unless a build opts in.
  ...(EMPLOYER_EXTRA_NAV
    ? [
        { label: t('nav.findTalent'), iconName: 'Users', href: '/(employer)/candidates', type: 'drawer', category: 'primary' },
        { label: t('nav.analytics'), iconName: 'Eye', href: '/(employer)/analytics', type: 'drawer', category: 'primary' },
        { label: t('nav.jobs'), iconName: 'Briefcase', href: '/(employer)/jobs', type: 'drawer', category: 'primary' },
      ] satisfies NavItem[]
    : []),
  { label: t('drawer.inbox'), iconName: 'MessageCircle', href: '/messages', type: 'push', badgeKey: 'messages', category: 'primary' },
  { label: t('drawer.notifications'), iconName: 'Bell', href: '/notifications', type: 'push', badgeKey: 'notifications', category: 'primary' },
  { label: t('nav.memory'), iconName: 'GitGraph', href: '/settings/memory', type: 'push', category: 'customize' },
];

export function getNavItemsForWorkspace(
  t: (k: string) => string,
  workspaceType: 'personal' | 'company',
  options: NavOptions = {},
): NavItem[] {
  return workspaceType === 'personal' ? personalNav(t, options) : companyNav(t);
}

// Rows for the native drawer's "Tools" list. Inbox and Notifications render as
// quick-action circles above it, and the chat destination gets its own "New
// chat" button - keeping a plain "Chat" row here as well made the new-chat
// action invisible (issue #249), so it is filtered out.
export function getDrawerToolItems(
  t: (k: string) => string,
  workspaceType: 'personal' | 'company',
  chatHref: string,
  options: NavOptions = {},
): NavItem[] {
  return getNavItemsForWorkspace(t, workspaceType, options).filter(
    (item) =>
      Boolean(item.href) &&
      !['MessageCircle', 'Bell'].includes(item.iconName) &&
      item.href !== chatHref,
  );
}

// Legacy aliases: use getNavItemsForWorkspace instead
export const getNavItems = (t: (k: string) => string) => personalNav(t);

// ─── Category grouping (native drawer + web sidebar) ─────────────────

export interface NavCategoryGroup {
  key: NavCategory;
  label: string;
  items: NavItem[];
}

const CATEGORY_ORDER: NavCategory[] = ['primary', 'customize'];

function getCategoryLabel(
  t: (k: string) => string,
  workspaceType: 'personal' | 'company',
  category: NavCategory,
): string {
  if (category === 'customize') return t('drawer.categoryCustomize');
  return workspaceType === 'company' ? t('drawer.categoryHiring') : t('drawer.categoryCareer');
}

// Splits a flat nav item list into labeled category groups (empty groups are
// dropped). Shared by the native drawer and web sidebar so both stay in sync
// on which item belongs to which category - they differ only in which items
// they pass in (native excludes inbox/notifications, rendered as its own
// quick-action row; web keeps them inline).
export function groupNavItemsByCategory(
  t: (k: string) => string,
  workspaceType: 'personal' | 'company',
  items: NavItem[],
): NavCategoryGroup[] {
  return CATEGORY_ORDER.map((key) => ({
    key,
    label: getCategoryLabel(t, workspaceType, key),
    items: items.filter((item) => (item.category ?? 'primary') === key),
  })).filter((group) => group.items.length > 0);
}

// ─── Web Nav Items (workspace-aware, with href) ─────────────────────

export function getNavItemsWebForWorkspace(
  t: (k: string) => string,
  workspaceType: 'personal' | 'company',
  options: NavOptions = {},
): WebNavItem[] {
  return workspaceType === 'personal' ? personalNav(t, options) : companyNav(t);
}

// Legacy alias
export const getNavItemsWeb = (t: (k: string) => string) => personalNav(t);

export function getWorkspaceThreadHref(workspace: WorkspaceSummary, threadId: string) {
  return getWorkspaceChatHref(workspace, threadId);
}

// ─── Thread grouping by date ─────────────────────────────────────────

interface DateLabels {
  today: string;
  yesterday: string;
  week: string;
  month: string;
}

export function groupThreadsByDate(
  threads: AgentThreadSummary[],
  labels: DateLabels,
): ThreadGroup[] {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 86_400_000);
  const startOfWeekAgo = new Date(startOfToday.getTime() - 7 * 86_400_000);

  const today: AgentThreadSummary[] = [];
  const yesterday: AgentThreadSummary[] = [];
  const week: AgentThreadSummary[] = [];
  const older: AgentThreadSummary[] = [];

  for (const thread of threads) {
    const d = new Date(thread.lastMessageAt || thread.createdAt);
    if (d >= startOfToday) today.push(thread);
    else if (d >= startOfYesterday) yesterday.push(thread);
    else if (d >= startOfWeekAgo) week.push(thread);
    else older.push(thread);
  }

  const groups: ThreadGroup[] = [];
  if (today.length) groups.push({ label: labels.today, items: today });
  if (yesterday.length) groups.push({ label: labels.yesterday, items: yesterday });
  if (week.length) groups.push({ label: labels.week, items: week });
  if (older.length) groups.push({ label: labels.month, items: older });
  return groups;
}

export function getDateLabels(t: (k: string) => string): DateLabels {
  return {
    today: t('labels.today'),
    yesterday: t('labels.yesterday'),
    week: t('labels.previous7Days'),
    month: t('labels.previous30Days'),
  };
}
